'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useConsent } from '@extern/consent/use-consent';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { getEnv } from '@/utils/env';
import { getFirstPartyVisitorId } from './first-party-visitor-id';
const BASE_OPTIONS = {
  api_host: 'https://posthog.extern.com',
  ui_host: 'https://us.posthog.com',
  person_profiles: 'identified_only',
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_exceptions: false,
  capture_performance: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  // Session recording is only enabled manually on specific pages (e.g. /portfolio)
  disable_session_recording: true
} as const;

/**
 * How long the app tree is allowed to wait on analytics before rendering without them.
 *
 * This provider wraps the whole product, so everything it waits for is a blank screen for the
 * visitor. Holding the tree until feature flags land is the pre-existing behaviour and worth
 * keeping for the common case — a returning visitor whose stored answer Cookiebot applies straight
 * away resolves well inside this window, and flag-driven UI renders decided rather than flipping.
 * But that wait now sits *behind* the consent answer, which for a first-time visitor is only
 * bounded by the CMP fallback (2s), and behind a flag request that can hang or never fire at all
 * when POSTHOG_KEY is missing. So the wait gets a ceiling: past it the product renders, flags
 * resolve when they resolve, and `PageView` gets to make its Segment `page` call — the landing URL
 * and UTM capture that must not depend on an analytics vendor being ready.
 */
const RENDER_GATE_TIMEOUT_MS = 1000;

/**
 * PostHog runs in one of two modes, chosen once per pageview from the consent answer.
 *
 * Without statistics consent it runs cookieless: `persistence: 'memory'` writes no cookie and no
 * localStorage entry, but capture stays on. That keeps A/B tests and kill switches working for
 * visitors who say no, and it is what sends their `identify` and `$feature_flag_called` events: an
 * opt-out here left them with no PostHog person and missing from experiment results (ENG-7476).
 * Everything that would collect on its own is off in `BASE_OPTIONS`, and session recording waits
 * for statistics consent in `useSessionRecording`. Surveys are off in this mode whatever the
 * route says: posthog-js loads them regardless of persistence, writes their seen and in-progress
 * state straight to localStorage, and sends `survey shown` / `survey sent`. Memory persistence
 * would mint a fresh distinct id on every pageview, so the client is bootstrapped with our own
 * first-party visitor id: same reason Segment gets `setAnonymousId`, same id, stable variants.
 *
 * With statistics consent it persists as usual. The upgrade path from one to the other is the
 * reload in `useConsentUpgradeReload` — switching persistence in place would leave PostHog holding
 * an id it minted while cookieless. That reload must not mint a new id either: the cookieless page
 * already sent `identify` and flag exposures under the visitor id, and a second id would count one
 * visitor as two people in experiment results. So the first persistent load is bootstrapped with
 * the same visitor id. Only the first: an anonymous bootstrap overwrites whatever PostHog has
 * stored, including an identified person, so a visitor PostHog already knows keeps their id.
 */
function getInitOptions(apiKey: string, hasStatisticsConsent: boolean, surveys: boolean) {
  if (hasStatisticsConsent) {
    const bootstrap = hasPersistedPostHogIdentity(apiKey) ? {
      featureFlags: {}
    } : {
      distinctID: getFirstPartyVisitorId(),
      featureFlags: {}
    };
    return {
      ...BASE_OPTIONS,
      disable_surveys: !surveys,
      bootstrap
    };
  }
  return {
    ...BASE_OPTIONS,
    disable_surveys: true,
    persistence: 'memory',
    bootstrap: {
      distinctID: getFirstPartyVisitorId(),
      featureFlags: {}
    }
  } as const;
}
const COOKIE_SEPARATOR = '; ';

/**
 * posthog-js's default name for its persistence entry, built the way the library builds it
 * (`parseName` in posthog-persistence): the token with `+`, `/` and `=` swapped out.
 */
function getPostHogPersistenceKey(apiKey: string): string {
  const token = apiKey.replaceAll('+', 'PL').replaceAll('/', 'SL').replaceAll('=', 'EQ');
  return `ph_${token}_posthog`;
}
function readCookieNames(): string[] {
  return document.cookie.split(COOKIE_SEPARATOR).map(row => row.split('=')[0] ?? '');
}
function readLocalStorageKeys(): string[] {
  try {
    return Array.from({
      length: window.localStorage.length
    }, (_, index) => window.localStorage.key(index) ?? '');
  } catch {
    return [];
  }
}

/**
 * Whether PostHog already stored an identity in this browser, in its cookie or its localStorage
 * entry (the default `localStorage+cookie` persistence).
 *
 * Matched without regard to case. posthog-js 1.407 keeps the token's case in the name, but a
 * miss here bootstraps the visitor id over a stored person, identified ones included, while a
 * false hit only costs the post-Accept continuity. So the check leans towards finding the entry.
 */
function hasPersistedPostHogIdentity(apiKey: string): boolean {
  const persistenceKey = getPostHogPersistenceKey(apiKey).toLowerCase();
  return [...readCookieNames(), ...readLocalStorageKeys()].some(name => name.toLowerCase() === persistenceKey);
}

/**
 * `surveys` gates PostHog's survey bundle (`surveys.js` plus its `/api/surveys/` call), which loads by
 * default. A survey popping up mid-lab on the public preview would interrupt a five-minute flow for a
 * visitor with no account, and it is another third-party payload on a route with no consent gate.
 */
export function PostHogProvider(t0) {
  const $ = _c(11);
  const {
    children,
    surveys: t1
  } = t0;
  const surveys = t1 === undefined ? true : t1;
  const {
    categories,
    isResolved
  } = useConsent();
  const [canRender, setCanRender] = useState(false);
  const isInitialized = useRef(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const timeout = setTimeout(() => setCanRender(true), RENDER_GATE_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== categories.statistics || $[3] !== isResolved || $[4] !== surveys) {
    t4 = () => {
      const apiKey = getEnv("POSTHOG_KEY");
      if (!(apiKey && isResolved) || isInitialized.current) {
        return;
      }
      isInitialized.current = true;
      posthog.init(apiKey, getInitOptions(apiKey, categories.statistics, surveys));
      posthog.onFeatureFlags(() => {
        setCanRender(true);
      });
    };
    $[2] = categories.statistics;
    $[3] = isResolved;
    $[4] = surveys;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== categories.statistics || $[7] !== isResolved) {
    t5 = [isResolved, categories.statistics];
    $[6] = categories.statistics;
    $[7] = isResolved;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  if (!canRender) {
    return null;
  }
  let t6;
  if ($[9] !== children) {
    t6 = <PHProvider client={posthog}>{children}</PHProvider>;
    $[9] = children;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}